if (!customElements.get('product-gallery-modal')) {
  customElements.define('product-gallery-modal', class ProductGalleryModal extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButton = this.querySelector('.product-gallery-modal__close');
      this.swiperElement = this.querySelector('.product-gallery-modal__swiper');
      this.previousButton = this.querySelector('[data-product-gallery-previous]');
      this.nextButton = this.querySelector('[data-product-gallery-next]');
      this.swiper = null;
      this.swiperPromise = null;

      this.querySelectorAll('[data-product-gallery-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    connectedCallback() {
      if (this.movedToBody) return;
      this.movedToBody = true;
      document.body.appendChild(this);
    }

    open(opener) {
      this.opener = opener;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      trapFocus(this, this.closeButton);

      this.initializeSwiper().then(() => {
        const mediaId = opener.dataset.mediaId;
        const fallbackIndex = Number(opener.dataset.galleryIndex || 0);
        const index = Array.from(this.swiper.slides)
          .findIndex((slide) => slide.dataset.mediaId === mediaId);

        this.swiper.update();
        this.swiper.slideTo(index >= 0 ? index : fallbackIndex, 0, false);
        this.activateMedia(this.swiper.slides[this.swiper.activeIndex]);
        this.swiper.keyboard.enable();
      });
    }

    close() {
      if (!this.hasAttribute('open')) return;

      window.pauseAllMedia();
      this.swiper?.keyboard.disable();
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.opener);
    }

    getSlides() {
      return Array.from(this.swiperElement.querySelectorAll('.swiper-slide'))
        .filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
    }

    loadSwiper() {
      if (window.Swiper) return Promise.resolve();
      if (window.freshempSwiperPromise) return window.freshempSwiperPromise;

      if (!document.querySelector('link[href*="swiper@12/swiper-bundle.min.css"]')) {
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.css';
        document.head.appendChild(stylesheet);
      }

      window.freshempSwiperPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return window.freshempSwiperPromise;
    }

    initializeSwiper() {
      if (this.swiper) return Promise.resolve(this.swiper);
      if (this.swiperPromise) return this.swiperPromise;

      this.swiperPromise = this.loadSwiper().then(() => {
        this.swiper = new Swiper(this.swiperElement, {
          direction: 'horizontal',
          allowTouchMove: true,
          simulateTouch: true,
          touchEventsTarget: 'container',
          grabCursor: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
          },
          slidesPerView: 1,
          spaceBetween: 0,
          loop: true,
          rewind: false,
          speed: 350,
          observer: true,
          observeParents: true,
          keyboard: {
            enabled: false,
            onlyInViewport: false,
          },
          navigation: {
            prevEl: this.previousButton,
            nextEl: this.nextButton,
          },
          on: {
            slideChangeTransitionStart: () => window.pauseAllMedia(),
            slideChange: (swiper) => this.activateMedia(swiper.slides[swiper.activeIndex]),
          },
        });

        return this.swiper;
      });

      return this.swiperPromise;
    }

    activateMedia(activeSlide) {
      if (!activeSlide) return;

      const deferredMedia = activeSlide.querySelector('deferred-media');
      const template = deferredMedia?.querySelector('template');

      if (deferredMedia && template?.content.querySelector('.js-youtube')) {
        deferredMedia.loadContent();
      }
    }
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-product-gallery-open]');
    if (!opener) return;

    const modal = document.querySelector(opener.dataset.productGalleryOpen);
    modal?.open(opener);
  });
}
